import React from 'react';
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';
import PDFHeader from './PDFHeader';

const styles = StyleSheet.create({
  page: { padding: 40, fontSize: 11, fontFamily: 'Helvetica', color: '#212529', backgroundColor: '#FFFFFF' },
  container: { width: 520, alignSelf: 'center' },
  detailsBlock: { alignSelf: 'flex-end', width: '60%', marginBottom: 16, backgroundColor: '#F9FAFB', padding: 10, borderRadius: 6, border: '1px solid #E5E7EB' },
  detailsRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 },
  headerRow: { flexDirection: 'row', justifyContent: 'space-between', gap: 12, marginBottom: 16 },
  card: { flex: 1, border: '1px solid #E5E7EB', borderRadius: 6, padding: 12, backgroundColor: '#FFFFFF' },
  cardTitle: { color: '#1E40AF', fontWeight: 700, fontSize: 12, marginBottom: 8, borderBottom: '1px solid #E5E7EB', paddingBottom: 4 },
  label: { color: '#6B7280', fontSize: 9, marginBottom: 2 },
  value: { fontWeight: 600, fontSize: 10, color: '#1F2937' },
  sectionTitle: { color: '#1E40AF', fontWeight: 700, marginTop: 16, marginBottom: 8, fontSize: 14, borderLeft: '3px solid #2563EB', paddingLeft: 8 },
  divider: { borderTop: '1px solid #E5E7EB', marginVertical: 12 },
  table: { borderTop: '1px solid #E5E7EB', marginTop: 8, borderLeft: '1px solid #E5E7EB', borderRight: '1px solid #E5E7EB' },
  tableRow: { flexDirection: 'row', borderBottom: '1px solid #E5E7EB' },
  tableHeader: { backgroundColor: '#F0F9FF', fontWeight: 700 },
  tableCell: { padding: 8, fontSize: 9, borderRight: '1px solid #E5E7EB' },
  tableHeaderCell: { padding: 8, fontSize: 9, borderRight: '1px solid #E5E7EB', fontWeight: 700, color: '#1E40AF' },
  drugCol: { width: '25%' },
  dosageCol: { width: '15%' },
  frequencyCol: { width: '15%' },
  durationCol: { width: '15%' },
  instructionsCol: { width: '30%' },
  footer: { marginTop: 24, paddingTop: 12, borderTop: '1px solid #E5E7EB', textAlign: 'center' },
  muted: { color: '#6B7280', fontSize: 9 },
  warningBox: { backgroundColor: '#FEF3C7', border: '1px solid #F59E0B', borderRadius: 4, padding: 8, marginTop: 12 },
  warningText: { fontSize: 9, color: '#92400E', fontStyle: 'italic' }
});

const formatDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A');

const PrescriptionPDF = ({ prescription, organization, clinic }) => {
  const patient = prescription?.patient || {};
  const doctorRaw = prescription?.doctor || prescription?.createdBy;
  const doctor = typeof doctorRaw === 'object' && doctorRaw !== null
    ? doctorRaw
    : { name: null };
  const rawName = doctor?.name || (doctor?.firstName && doctor?.lastName ? `${doctor.firstName} ${doctor.lastName}`.trim() : null) || 'N/A';
  const doctorName = (rawName !== 'N/A' && /^Dr\.?\s/i.test(rawName)) ? rawName : (rawName !== 'N/A' ? `Dr. ${rawName}` : rawName);
  const clinicInfo = clinic || patient?.registeredClinic || {};
  const meds = Array.isArray(prescription?.medications) ? prescription.medications : [];

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.container}>
          {/* Header with Clinic Info */}
          <PDFHeader 
            organization={organization}
            clinic={clinicInfo}
            documentTitle="Prescription"
            documentSubtitle="Medical Prescription"
          />

          {/* Prescription Details */}
          <View style={styles.detailsBlock}>
            <View style={styles.detailsRow}>
              <Text style={styles.label}>Prescription No.</Text>
              <Text style={{ fontWeight: 600 }}>{prescription?.prescriptionNumber || 'N/A'}</Text>
            </View>
            <View style={styles.detailsRow}>
              <Text style={styles.label}>Date</Text>
              <Text style={{ fontWeight: 600 }}>{formatDate(prescription?.date || prescription?.createdAt)}</Text>
            </View>
            {prescription?.followUpDate && (
              <View style={styles.detailsRow}>
                <Text style={styles.label}>Follow-up Date</Text>
                <Text style={{ fontWeight: 600 }}>{formatDate(prescription.followUpDate)}</Text>
              </View>
            )}
          </View>

          {/* Patient & Doctor Info */}
          <View style={styles.headerRow}>
            {/* Patient Info */}
            <View style={styles.card}>
              <Text style={styles.cardTitle}>Patient Information</Text>
              {patient?.name && <Text style={styles.value}>{patient.name}</Text>}
              {patient?.patientId && (
                <Text style={{ fontSize: 9, color: '#6B7280', marginTop: 2 }}>
                  ID: {patient.patientId}
                </Text>
              )}
              {patient?.phone && (
                <Text style={{ fontSize: 9, color: '#6B7280', marginTop: 4 }}>
                  Phone: {patient.phone}
                </Text>
              )}
              {patient?.email && (
                <Text style={{ fontSize: 9, color: '#6B7280', marginTop: 2 }}>
                  Email: {patient.email}
                </Text>
              )}
              {patient?.dateOfBirth && (
                <Text style={{ fontSize: 9, color: '#6B7280', marginTop: 2 }}>
                  DOB: {formatDate(patient.dateOfBirth)}
                </Text>
              )}
            </View>

            {/* Doctor Info */}
            <View style={styles.card}>
              <Text style={styles.cardTitle}>Prescribing Doctor</Text>
              <Text style={styles.value}>{doctorName}</Text>
              {(doctor?.licenseNumber != null && String(doctor.licenseNumber).trim() !== '') && (
                <Text style={{ fontSize: 9, color: '#6B7280', marginTop: 2 }}>
                  License: {doctor.licenseNumber}
                </Text>
              )}
              {doctor?.email && (
                <Text style={{ fontSize: 9, color: '#6B7280', marginTop: 4 }}>
                  Email: {doctor.email}
                </Text>
              )}
              {doctor?.phone && (
                <Text style={{ fontSize: 9, color: '#6B7280', marginTop: 2 }}>
                  Phone: {doctor.phone}
                </Text>
              )}
              {doctor?.qualification && (
                <Text style={{ fontSize: 9, color: '#6B7280', marginTop: 2 }}>
                  {doctor.qualification}
                </Text>
              )}
            </View>
          </View>

          {/* Diagnosis */}
          {prescription?.diagnosis && (
            <>
              <View style={styles.divider} />
              <Text style={styles.sectionTitle}>Diagnosis</Text>
              <View style={{ backgroundColor: '#F9FAFB', padding: 10, borderRadius: 4, border: '1px solid #E5E7EB' }}>
                <Text style={{ fontSize: 10, lineHeight: 1.5 }}>{prescription.diagnosis}</Text>
              </View>
            </>
          )}

          {/* Medications Table */}
          {meds.length > 0 && (
            <>
              <View style={styles.divider} />
              <Text style={styles.sectionTitle}>Medications</Text>
              <View style={styles.table}>
                {/* Table Header */}
                <View style={[styles.tableRow, styles.tableHeader]}>
                  <Text style={[styles.tableHeaderCell, styles.drugCol]}>Drug Name</Text>
                  <Text style={[styles.tableHeaderCell, styles.dosageCol]}>Dosage</Text>
                  <Text style={[styles.tableHeaderCell, styles.frequencyCol]}>Frequency</Text>
                  <Text style={[styles.tableHeaderCell, styles.durationCol]}>Duration</Text>
                  <Text style={[styles.tableHeaderCell, styles.instructionsCol]}>Instructions</Text>
                </View>
                {/* Table Rows */}
                {meds.map((m, idx) => (
                  <View style={styles.tableRow} key={idx}>
                    <Text style={[styles.tableCell, styles.drugCol]}>
                      {m.drug?.name || 'N/A'}{m.drug?.strength ? ` (${m.drug.strength})` : ''}
                    </Text>
                    <Text style={[styles.tableCell, styles.dosageCol]}>{m.dosage ?? m.dose ?? '-'}</Text>
                    <Text style={[styles.tableCell, styles.frequencyCol]}>{m.frequency || '-'}</Text>
                    <Text style={[styles.tableCell, styles.durationCol]}>{m.duration || '-'}</Text>
                    <Text style={[styles.tableCell, styles.instructionsCol]}>{m.instructions || '-'}</Text>
                  </View>
                ))}
              </View>
            </>
          )}

          {/* General Instructions */}
          {prescription?.instructions && (
            <>
              <View style={styles.divider} />
              <Text style={styles.sectionTitle}>General Instructions</Text>
              <View style={{ backgroundColor: '#F9FAFB', padding: 10, borderRadius: 4, border: '1px solid #E5E7EB' }}>
                <Text style={{ fontSize: 10, lineHeight: 1.5 }}>{prescription.instructions}</Text>
              </View>
            </>
          )}

          {/* Warning Box */}
          <View style={styles.warningBox}>
            <Text style={styles.warningText}>
              Please take medications as prescribed. Do not discontinue without consulting your doctor.
            </Text>
          </View>

          {/* Issued Information */}
          {prescription?.isIssuedToPatient && (
            <View style={{ marginTop: 12, padding: 8, backgroundColor: '#F0FDF4', borderRadius: 4, border: '1px solid #86EFAC' }}>
              <Text style={{ fontSize: 9, color: '#166534' }}>
                Issued to patient on {formatDate(prescription.issuedToPatientDate)} by {prescription?.issuedBy?.name || 'N/A'}
              </Text>
            </View>
          )}

          {/* Footer */}
          <View style={styles.footer}>
            <Text style={styles.muted}>This prescription was generated electronically by DentalOS</Text>
            <Text style={[styles.muted, { marginTop: 4 }]}>
              {new Date().toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })} at {new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
            </Text>
            {doctorName !== 'N/A' && (
              <View style={{ marginTop: 12, paddingTop: 8, borderTop: '1px solid #E5E7EB' }}>
                <Text style={{ fontSize: 10, fontWeight: 600, marginTop: 8 }}>{doctorName}</Text>
                <Text style={{ fontSize: 9, color: '#6B7280', marginTop: 2 }}>Authorized Signature</Text>
              </View>
            )}
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default PrescriptionPDF;
